/**
 * Enums shared across schemas. Mirror the Postgres enum values defined in
 * migration 0001 (T-007). Adding a value here without a corresponding migration
 * is a defect; the Postgres enum is the source of truth at the database layer.
 *
 * See ARCH-001 §4 and PRD-003 §5.3 (DATA-04, DATA-05).
 */

import { z } from 'zod';

/**
 * Subscriber tier. PRD-003 GTM-01 + DATA-01.
 */
export const tierSchema = z.enum(['studio', 'pro']);
export type Tier = z.infer<typeof tierSchema>;

/**
 * Subscriber lifecycle status. PRD-003 DATA-01.
 */
export const subscriberStatusSchema = z.enum(['active', 'paused', 'canceled']);
export type SubscriberStatus = z.infer<typeof subscriberStatusSchema>;

/**
 * Onboarding status. PRD-003 DATA-18.
 */
export const onboardingStatusSchema = z.enum([
  'pending',
  'in_progress',
  'submitted',
  'eric_reviewed',
]);
export type OnboardingStatus = z.infer<typeof onboardingStatusSchema>;

/**
 * Task type. PRD-003 DATA-05.
 *
 * Sixteen values grouped for the intake dropdown:
 * - Strategy & positioning: positioning_clarity, icp_synthesis, competitive_landscape, pricing_strategy, gtm_strategy
 * - Messaging & voice: messaging_architecture, voice_encoding, pitch_deck_review, landing_page_copy
 * - Product & research: product_strategy, user_research_synthesis, prototyping
 * - AI & tooling: ai_workflow_design, ai_coaching, internal_tool_build
 * - Other: other (generic markdown renderer)
 */
export const taskTypeSchema = z.enum([
  // Strategy & positioning
  'positioning_clarity',
  'icp_synthesis',
  'competitive_landscape',
  'pricing_strategy',
  'gtm_strategy',
  // Messaging & voice
  'messaging_architecture',
  'voice_encoding',
  'pitch_deck_review',
  'landing_page_copy',
  // Product & research
  'product_strategy',
  'user_research_synthesis',
  'prototyping',
  // AI & tooling
  'ai_workflow_design',
  'ai_coaching',
  'internal_tool_build',
  // Other
  'other',
]);
export type TaskType = z.infer<typeof taskTypeSchema>;

/**
 * Phase 1 templated types that ship type-specific renderers. PRD-003 DATA-05, TECH-09.
 */
export const phaseOneTemplatedTypes = [
  'competitive_landscape',
  'icp_synthesis',
  'voice_encoding',
  'messaging_architecture',
  'pitch_deck_review',
] as const satisfies readonly TaskType[];

/**
 * Task status. PRD-003 DATA-04.
 *
 * Ten lifecycle values plus 'split' (parent rows after PR-39 split) for an
 * effective enum size of 11. agent_draft and eric_reviewing apply only to
 * templated types.
 */
export const taskStatusSchema = z.enum([
  'intake_shape_check',
  'backlog',
  'ready_to_pull',
  'pulled',
  'in_progress',
  'agent_draft',
  'eric_reviewing',
  'ready_to_deliver',
  'delivered',
  'approved',
  'split',
]);
export type TaskStatus = z.infer<typeof taskStatusSchema>;

/**
 * Notification type. PRD-003 PR-46 + DATA-15.
 */
export const notificationTypeSchema = z.enum([
  'onboarding_completed',
  // Post-Stripe-checkout onboarding-invite note (PR-06; T-085). Matches the
  // migration 0021 additive enum value. Distinct from the Supabase Auth
  // magic-link template (`supabase/templates/magic_link.html`).
  'onboarding_invite',
  'welcome_view_ready',
  'task_pulled',
  'deliverable_ready',
  'task_approved',
  'waitlist_update',
  // Waitlist signup auto-reply (PR-46; T-085). Matches the migration 0022
  // additive enum value. Distinct from `waitlist_update` (the "spot has opened"
  // invite).
  'waitlist_confirm',
  'pause_confirmed',
  'cancel_confirmed',
  'revision_requested',
  'report_ready',
  // GDPR account-deletion deliverables backup bundle (ARCH-014; the
  // subscriber-facing email sent BEFORE the wipe). Matches the migration 0017
  // public.notification_type additive value.
  'account_deletion_backup',
  'stripe_subscription_pending_link',
  'capacity_race_overfill',
  'stripe_divergence',
  // Operator-internal: monthly progression-report cron queued a draft (T-079;
  // PR-21 / TECH-21). Matches the migration 0019 additive enum value. No
  // subscriber-facing template; the bell + /eric/reports queue read this.
  'progression_report_draft_queued',
  // OS lifecycle beats (PRD-004 scoping-pass §4 PR-OS-01 / §5; OS Batch 2).
  // Match the migration 0028 additive enum values. Owner-facing but FEED-ONLY
  // in V1: they are NOT in the email dispatcher's SUBSCRIBER_NOTIFICATION_TYPES,
  // so the dispatcher marks them feed-only/no-email (the R-OS3 posture: the
  // in-app status line is the source of truth and email is secondary until the
  // sending domain is verified). Email templates are a follow-up.
  'os_acknowledged',
  'os_scoped',
  'os_handoff_ready',
  'os_installed',
  // Input-requests cycle (PRD-005 / ARCH-028; migration 0034 additive enum
  // split, the 0028 pattern). FEED-ONLY in V1 like the OS beats above: not in
  // the email dispatcher's SUBSCRIBER_NOTIFICATION_TYPES (R-OS3 posture, email
  // templates a follow-up). `input_requested` is recipient-facing (Eric sent a
  // request); `input_submitted` is operator-internal (the recipient answered).
  'input_requested',
  'input_submitted',
]);
export type NotificationType = z.infer<typeof notificationTypeSchema>;

/**
 * Revision request status. PRD-003 PR-54.
 */
export const revisionStatusSchema = z.enum([
  'pending',
  'accepted',
  'declined',
  'completed',
]);
export type RevisionStatus = z.infer<typeof revisionStatusSchema>;

/**
 * Coaching depth selector. PRD-003 PR-45, AI-12.
 */
export const coachingDepthSchema = z.enum([
  'recommend',
  'show_me',
  'build_it_with_me',
]);
export type CoachingDepth = z.infer<typeof coachingDepthSchema>;

/**
 * Task intake mode. PRD-003 PR-11.
 */
export const intakeModeSchema = z.enum(['form', 'chat']);
export type IntakeMode = z.infer<typeof intakeModeSchema>;

/**
 * Meta-note section state. PRD-003 PR-37.
 */
export const metaNoteSectionStateSchema = z.enum([
  'not_started',
  'in_progress',
  'draft_done',
  'ready_to_send',
]);
export type MetaNoteSectionState = z.infer<typeof metaNoteSectionStateSchema>;

/**
 * Source citation type. PRD-003 DATA-17.
 */
export const sourceTypeSchema = z.enum(['please-family', 'external']);
export type SourceType = z.infer<typeof sourceTypeSchema>;

/**
 * Email frequency for the subscriber notification preference. PRD-003 PR-46.
 */
export const emailFrequencySchema = z.enum(['immediate', 'daily', 'weekly', 'off']);
export type EmailFrequency = z.infer<typeof emailFrequencySchema>;

/**
 * Video provider for Pro deliverables. PRD-003 TECH-15 + DATA-06.
 */
export const videoProviderSchema = z.enum(['loom', 'youtube', 'vimeo']);
export type VideoProvider = z.infer<typeof videoProviderSchema>;
